import { Link, useNavigate } from 'react-router';
import { LogOutIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useT } from '@/lib/i18n';
import { useSession } from './session';

/**
 * Topbar user chip — avatar, name, role, sign out.
 *
 * Placement follows NAFIS_Assessment_Admin_Walkthrough.html: identity sits in
 * the TOP BAR, not in the sidebar footer where the shadcn kit puts it by
 * default.
 *
 * The identity is whatever was typed on the login screen — no credential was
 * verified — so the role reads "unverified" rather than presenting a
 * convincing account. Sign out clears the stub session and returns to the
 * login screen; in Phase 4 it also revokes the refresh token server-side.
 */
export function UserChip() {
  const { user, signOut } = useSession();
  const navigate = useNavigate();
  const t = useT();

  // AuthGuard means this only renders inside the console, i.e. with a session.
  if (!user) return null;

  function handleSignOut() {
    // Navigate immediately rather than awaiting: `signOut` clears the local
    // session before it calls the API, so the user is signed out here whatever
    // the network does. The awaited half only revokes the refresh token.
    void signOut();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex items-center gap-2.5 rounded-full border py-1 ps-1 pe-2.5">
      <Link to="/profile" className="flex items-center gap-2" aria-label={t('chrome.openProfile')}>
        <Avatar className="size-8">
          {/* Gold gradient, per the walkthrough. It was a flat muted circle. */}
          <AvatarFallback className="brand-avatar text-[12.5px]">{user.initials}</AvatarFallback>
        </Avatar>
        <div className="hidden text-start leading-tight sm:block">
          <p className="text-xs font-semibold">{user.name}</p>
          <p className="text-muted-foreground text-[11px]">
            {t(user.role === 'admin' ? 'role.admin' : 'role.analyst')}
          </p>
        </div>
      </Link>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={t('chrome.signOut')}
            onClick={handleSignOut}
          >
            <LogOutIcon className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('chrome.signOut')}</TooltipContent>
      </Tooltip>
    </div>
  );
}
